<template>
  <div class="rb-ob-form rb-match-page rb-tx-page">
    <header>
      <h1 class="rb-ob-title">{{ $t('intake.treatment.notifyTitle') }}</h1>
    </header>

    <div class="rb-tx-panel">
      <p>{{ $t('intake.treatment.notifyBody') }}</p>
    </div>

    <p v-if="needsLogin" class="rb-ob-status" role="status">{{ $t('intake.portal.needsLogin') }}</p>
    <div v-else-if="loading" class="rb-ob-status">{{ $t('intake.portal.loading') }}</div>
    <section v-else-if="!needsLogin" aria-labelledby="notificationUpdatesTitle">
      <h2 id="notificationUpdatesTitle">{{ lang === 'fa' ? 'تازه‌های مسیر من' : 'Updates to my care' }}</h2>
      <p v-if="loadError" role="alert">{{ lang === 'fa' ? 'دریافت اعلان‌ها انجام نشد.' : 'Notifications could not be loaded.' }} <button type="button" class="btn btn-secondary" @click="bootstrap">{{ lang === 'fa' ? 'تلاش دوباره' : 'Try again' }}</button></p>
      <div v-else-if="notifications.length" class="rb-tx-notify-list">
        <button v-for="item in notifications" :key="item.id" type="button" class="rb-tx-notify-item" :disabled="!!opening || !notificationTarget(item)" @click="openNotification(item)">
          <span class="rb-tx-notify-t">{{ notificationTitle(item) }} <small v-if="!item.read_at">{{ lang === 'fa' ? 'جدید' : 'New' }}</small></span>
          <time>{{ notificationWhen(item) }}</time>
        </button>
      </div>
      <p v-else>{{ lang === 'fa' ? 'اعلان تازه‌ای نداری.' : 'There are no updates yet.' }}</p>
      <p v-if="openError" role="alert">{{ openError }}</p>
      <nav v-if="lastPage > 1" class="actions" :aria-label="lang === 'fa' ? 'صفحه‌بندی اعلان‌ها' : 'Notification pages'">
        <button type="button" :disabled="page <= 1 || !!opening" @click="loadPage(page - 1)">{{ lang === 'fa' ? 'قبلی' : 'Previous' }}</button>
        <span>{{ page }} / {{ lastPage }}</span>
        <button type="button" :disabled="page >= lastPage || !!opening" @click="loadPage(page + 1)">{{ lang === 'fa' ? 'بعدی' : 'Next' }}</button>
      </nav>
    </section>

    <h2 v-if="!loading && !needsLogin">{{ lang === 'fa' ? 'یادآوری پرسشنامه‌ها' : 'Assessment reminders' }}</h2>
    <div v-if="!loading && !needsLogin && items.length" class="rb-tx-notify-list" :aria-label="$t('intake.treatment.notifyListAria')">
      <button
          v-for="item in items"
          :key="item.id"
          class="rb-tx-notify-item"
          type="button"
          @click="open(item)"
      >
        <span class="rb-tx-notify-t">{{ item.title }}</span>
        <span class="rb-tx-notify-m">
          <span>{{ item.when }}</span>
          <span>{{ item.state }}</span>
        </span>
      </button>
    </div>
    <div v-else-if="!loading && !needsLogin" class="rb-tx-panel">
      <p>{{ $t('intake.treatment.notifyEmpty') }}</p>
    </div>

    <div class="rb-ob-actions">
      <button class="rb-ob-submit" type="button" @click="back">
        {{ $t('intake.treatment.notifyBack') }}
      </button>
      <p class="rb-ob-status" role="status" aria-live="polite"></p>
      <div class="rb-ob-trust">
        <p>{{ $t('intake.trustPath') }}</p>
        <p>{{ $t('authUi.familyNote') }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import {
  formatSessionListTitle,
  hasAuthToken,
  listPendingAssessments,
  formatSupportWhen,
} from '@/intake/portalApi'
import {fetchClientNotifications, notificationRoute, readClientNotification} from '@/intake/clientNotifications'

export default {
  name: 'NotifyStep',
  data() {
    return {
      loading: true,
      needsLogin: false,
      pending: [],
      notifications: [], page: 1, lastPage: 1, loadError: false, openError: '', opening: '', generation: 0,
    }
  },
  computed: {
    lang() {
      return this.$route.params.lang || 'fa'
    },
    items() {
      return this.pending.map((row) => {
        const isOrs = row.instrument === 'ORS' || row.instrument === 'CORS'
        const when = row.session
          ? formatSessionListTitle(row.session, this.lang)
          : (row.sessionNumber
            ? this.$t('intake.treatment.notifySession', {n: row.sessionNumber})
            : (row.label || this.$t('intake.treatment.notifyPendingWhen')))
        return {
          id: row.id,
          sessionId: row.sessionId,
          route: isOrs ? 'intakeOutcomeOrs' : 'intakeOutcomeSrs',
          title: isOrs
            ? this.$t('intake.treatment.notifyOrsTitle')
            : this.$t('intake.treatment.notifySrsTitle'),
          when,
          state: row.status === 'missing'
            ? this.$t('intake.treatment.notifyMissing')
            : this.$t('intake.treatment.notifyPendingState'),
        }
      })
    },
  },
  mounted() {
    this.bootstrap()
  },
  beforeDestroy() { this.generation += 1 },
  methods: {
    bootstrap() {
      return this.loadPage(1)
    },
    loadPage(page) {
      const generation = ++this.generation
      this.loading = true
      this.loadError = false
      this.openError = ''
      this.notifications = []
      this.needsLogin = !hasAuthToken()
      if (this.needsLogin) {
        this.loading = false
        return
      }
      const reminders = listPendingAssessments().then(rows=>{if(generation===this.generation)this.pending=rows}).catch(()=>{if(generation===this.generation)this.pending=[]})
      const updates = fetchClientNotifications(page).then(data=>{if(generation!==this.generation)return;this.notifications=data.data;this.page=data.current_page;this.lastPage=data.last_page}).catch(()=>{if(generation===this.generation)this.loadError=true})
      return Promise.all([reminders,updates]).finally(()=>{if(generation===this.generation)this.loading=false})
    },
    notificationTarget(item) { return notificationRoute(item,this.lang) },
    notificationTitle(item) {
      const titles = this.lang==='fa' ? {SUPPORT_REPLY:'پاسخ تازهٔ پشتیبانی',THERAPIST_MESSAGE:'پیام تازهٔ درمانگر',SESSION_HELD:'نتیجهٔ جلسه ثبت شد'} : {SUPPORT_REPLY:'New support reply',THERAPIST_MESSAGE:'New therapist message',SESSION_HELD:'Session outcome recorded'}
      return titles[item.type] || (this.lang==='fa'?'تازه‌ای در مسیر درمان':'Care update')
    },
    notificationWhen(item) { return formatSupportWhen(item.created_at,this.lang)||'—' },
    openNotification(item) {
      const target=this.notificationTarget(item)
      if(this.opening||!target)return
      const generation=this.generation
      this.opening=String(item.id);this.openError=''
      return readClientNotification(item.id).then(updated=>{if(generation!==this.generation)return;Object.assign(item,updated);return this.$router.push(target)})
        .catch(()=>{if(generation===this.generation)this.openError=this.lang==='fa'?'باز کردن اعلان انجام نشد. دوباره تلاش کن.':'This update could not be opened. Try again.'})
        .finally(()=>{if(generation===this.generation)this.opening=''})
    },
    open(item) {
      this.$router.push({
        name: item.route,
        params: {lang: this.lang},
        query: {sessionId: String(item.sessionId)},
      })
    },
    back() {
      this.$router.push({name: 'portalHome', params: {lang: this.lang}})
    },
  },
}
</script>
